/**
 * Mobile Product Page Enhancements
 * Improves the mobile shopping experience with smart interactions
 */

class MobileProductEnhancements {
  constructor() {
    if (window.innerWidth > 749) return;

    this.init();
  }

  init() {
    this.setupStickyAddToCart();
    this.setupVariantImageUpdate();
    this.setupTouchFeedback();
    this.setupImageZoomHint();
  }

  /**
   * Sticky Add to Cart Bar
   * Shows a fixed bar at the bottom when the main add-to-cart button is scrolled out of view
   */
  setupStickyAddToCart() {
    const mainAddToCartButton = document.querySelector('.product-form__submit');
    const stickyBar = document.querySelector('.sticky-add-to-cart');

    if (!mainAddToCartButton || !stickyBar) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('sticky-add-to-cart--visible');
          } else {
            stickyBar.classList.add('sticky-add-to-cart--visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    observer.observe(mainAddToCartButton);

    // Sync sticky button with main form
    const stickyButton = stickyBar.querySelector('.sticky-add-to-cart__button');
    if (stickyButton) {
      stickyButton.addEventListener('click', () => {
        mainAddToCartButton.click();
        // Scroll to top smoothly to show confirmation
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  /**
   * Update Sticky Bar on Variant Change
   * Updates the sticky bar image and price when variant changes
   */
  setupVariantImageUpdate() {
    const stickyBar = document.querySelector('.sticky-add-to-cart');
    if (!stickyBar) return;

    // Listen for variant change events
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      // Update price
      const priceElement = stickyBar.querySelector('.sticky-add-to-cart__price');
      if (priceElement && variant.price) {
        priceElement.textContent = this.formatMoney(variant.price);
      }

      // Update variant name
      const variantElement = stickyBar.querySelector('.sticky-add-to-cart__variant');
      if (variantElement && variant.title && variant.title !== 'Default Title') {
        variantElement.textContent = variant.title;
      }

      // Update image
      const imageElement = stickyBar.querySelector('.sticky-add-to-cart__image img');
      if (imageElement && variant.featured_image) {
        imageElement.src = variant.featured_image.src;
      }

      // Update button state
      const button = stickyBar.querySelector('.sticky-add-to-cart__button');
      if (button) {
        if (variant.available) {
          button.disabled = false;
          button.textContent = 'Add to Cart';
        } else {
          button.disabled = true;
          button.textContent = 'Sold Out';
        }
      }
    });
  }

  /**
   * Enhanced Touch Feedback
   * Provides visual feedback for touch interactions
   */
  setupTouchFeedback() {
    const touchElements = document.querySelectorAll(`
      .product-form__submit,
      .variant-picker__option-button,
      .quantity-selector__button,
      .discount-banner__copy-button
    `);

    touchElements.forEach(element => {
      element.addEventListener('touchstart', () => {
        element.style.opacity = '0.7';
      });

      element.addEventListener('touchend', () => {
        setTimeout(() => {
          element.style.opacity = '';
        }, 100);
      });

      element.addEventListener('touchcancel', () => {
        element.style.opacity = '';
      });
    });
  }

  /**
   * Image Zoom Hint
   * Shows a hint that images can be tapped to zoom
   */
  setupImageZoomHint() {
    const productImages = document.querySelectorAll('.product-media');
    if (productImages.length === 0) return;

    let hintShown = false;

    productImages.forEach((image, index) => {
      if (index === 0 && !hintShown) {
        // Show hint on first image only
        const hint = this.createZoomHint();
        image.style.position = 'relative';
        image.appendChild(hint);

        setTimeout(() => {
          hint.style.opacity = '0';
          setTimeout(() => {
            hint.remove();
          }, 300);
        }, 3000);

        hintShown = true;
      }
    });
  }

  /**
   * Create Zoom Hint Element
   */
  createZoomHint() {
    const hint = document.createElement('div');
    hint.className = 'mobile-zoom-hint';
    hint.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M8 2a6 6 0 104.32 10.18l4.25 4.25a1 1 0 001.42-1.42l-4.25-4.25A6 6 0 008 2zm0 2a4 4 0 110 8 4 4 0 010-8z"/>
        <path d="M8 6v4M6 8h4" stroke="currentColor" stroke-width="1.5"/>
      </svg>
      <span>Tap to zoom</span>
    `;
    hint.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      display: flex;
      align-items: center;
      gap: 6px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 500;
      z-index: 10;
      pointer-events: none;
      transition: opacity 0.3s ease;
    `;
    return hint;
  }

  /**
   * Format Money
   * Formats price values
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  /**
   * Show Toast Notification
   */
  showToast(message, duration = 3000) {
    const toast = document.createElement('div');
    toast.className = 'mobile-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 16px;
      right: 16px;
      background: var(--color-foreground);
      color: var(--color-background);
      padding: 14px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      z-index: 1000;
      animation: slideUpFade 0.4s ease-out;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(20px)';
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, duration);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MobileProductEnhancements();
  });
} else {
  new MobileProductEnhancements();
}

// Re-initialize on section rendering (for theme editor)
document.addEventListener('shopify:section:load', () => {
  new MobileProductEnhancements();
});
